/**
 * PlaygroundApp.tsx
 * Main playground wrapper with theme state management
 * 
 * Provides theme context to all playground components
 */

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { usePlaygroundTheme } from './shared/usePlaygroundTheme';
import { PlaygroundThemeContext } from './shared/PlaygroundThemeContext';
import { PlaygroundNavbar } from './shared/PlaygroundNavbar';
import { PlaygroundDashboard } from './PlaygroundDashboard';
import { ComponentsListing } from './ComponentsListing';
import { LinkExamples } from './components/LinkExamples';
import { BadgeExamples } from './components/BadgeExamples';
import { CardExamples } from './components/CardExamples';
import { ChipExamples } from './components/ChipExamples';
import { DividerExamples } from './components/DividerExamples';
import { ButtonExamples } from './components/ButtonExamples';
import { ButtonGroupExamples } from './components/ButtonGroupExamples';
import { NavbarExamples } from './components/NavbarExamples';
import { BreadcrumbsExamples } from './components/BreadcrumbsExamples';
import { DrawerExamples } from './components/DrawerExamples';
import { AccordionExamples } from './components/AccordionExamples';
import { TabsExamples } from './components/TabsExamples';
import { InputExamples } from './components/InputExamples';
import { NumberInputExamples } from './components/NumberInputExamples';
import { TextAreaExamples } from './components/TextAreaExamples';
import { SelectExamples } from './components/SelectExamples';
import { DropdownExamples } from './components/DropdownExamples';
import { ListboxExamples } from './components/ListboxExamples';
import { CheckboxExamples } from './components/CheckboxExamples';
import { RadioGroupExamples } from './components/RadioGroupExamples';
import { SwitchExamples } from './components/SwitchExamples';
import { SliderExamples } from './components/SliderExamples';
import { DateInputExamples } from './components/DateInputExamples';
import { TimeInputExamples } from './components/TimeInputExamples';
import { CalendarExamples } from './components/CalendarExamples';
import { RangeCalendarExamples } from './components/RangeCalendarExamples';
import { DatePickerExamples } from './components/DatePickerExamples';
import { DateRangePickerExamples } from './components/DateRangePickerExamples';
import { ModalExamples } from './components/ModalExamples';
import { AlertExamples } from './components/AlertExamples';
import { ToastExamples } from './components/ToastExamples';
import { TooltipExamples } from './components/TooltipExamples';
import { PopoverExamples } from './components/PopoverExamples';
import { ProgressExamples } from './components/ProgressExamples';
import { CircularProgressExamples } from './components/CircularProgressExamples';
import { SpinnerExamples } from './components/SpinnerExamples';
import { SkeletonExamples } from './components/SkeletonExamples';
import { CodeExamples } from './components/CodeExamples';
import { ImageExamples } from './components/ImageExamples';
import { KbdExamples } from './components/KbdExamples';
import { ScrollShadowExamples } from './components/ScrollShadowExamples';
import { SplitPaneExamples } from './components/SplitPaneExamples';
import { PaginationExamples } from './components/PaginationExamples';
import { SnippetExamples } from './components/SnippetExamples';
import { TableExamples } from './components/TableExamples';
import { NavigationComponents, LayoutComponents, FormControls, FeedbackComponents, DataDisplay } from './categories';
import { V4ThemePlayground } from './themes/V4ThemePlayground';
import { AGGridPlayground } from './data/AGGridPlayground';


/**
 * Main playground app with centralized theme management
 */
export const PlaygroundApp: React.FC = () => {
  const themeState = usePlaygroundTheme();

  // Create wrapper function to handle string type from components
  const handleThemeChange = (theme: string) => {
    themeState.setTheme(theme as 'light' | 'dark' | 'auto');
  };

  return (
    <PlaygroundThemeContext.Provider value={themeState}>
      {/* Persistent Navigation Bar */}
      <PlaygroundNavbar
        currentTheme={themeState.currentTheme}
        onThemeChange={handleThemeChange}
      />
      
      {/* Main Content Area */}
      <div className="min-h-screen bg-background">
        <Routes>
          {/* Main Playground Dashboard - Navigation Hub */}
          <Route 
            index
            element={
              <PlaygroundDashboard />
            } 
          />
        
        {/* Components Section */}
        <Route 
          path="components" 
          element={<ComponentsListing />} 
        />
        
        {/* Category Pages */}
        <Route 
          path="components/navigation" 
          element={<NavigationComponents />} 
        />
        <Route 
          path="components/layout" 
          element={<LayoutComponents />} 
        />
        <Route 
          path="components/forms" 
          element={<FormControls />} 
        />
        <Route 
          path="components/feedback" 
          element={<FeedbackComponents />} 
        />
        <Route 
          path="components/data-display" 
          element={<DataDisplay />} 
        />
        
        {/* Navigation Component Examples */}
        <Route 
          path="components/navigation/link" 
          element={<LinkExamples />} 
        />
        <Route 
          path="components/navigation/navbar" 
          element={<NavbarExamples />} 
        />
        <Route 
          path="components/navigation/breadcrumbs" 
          element={<BreadcrumbsExamples />} 
        />
        <Route 
          path="components/navigation/tabs" 
          element={<TabsExamples />} 
        />
        <Route 
          path="components/navigation/pagination" 
          element={<PaginationExamples />} 
        />
        
        {/* Layout Component Examples */}
        <Route 
          path="components/layout/badge" 
          element={<BadgeExamples />} 
        />
        <Route 
          path="components/layout/card" 
          element={<CardExamples />} 
        />
        <Route 
          path="components/layout/chip" 
          element={<ChipExamples />} 
        />
        <Route 
          path="components/layout/divider" 
          element={<DividerExamples />} 
        />
        <Route 
          path="components/layout/drawer" 
          element={<DrawerExamples />} 
        />
        <Route 
          path="components/layout/accordion" 
          element={<AccordionExamples />} 
        />
        <Route
          path="components/layout/scroll-shadow"
          element={<ScrollShadowExamples />}
        />
        <Route
          path="components/layout/split-pane"
          element={<SplitPaneExamples />}
        />

        {/* Form Control Examples */}
        <Route 
          path="components/forms/button" 
          element={<ButtonExamples />} 
        />
        <Route 
          path="components/forms/button-group" 
          element={<ButtonGroupExamples />} 
        />
        <Route 
          path="components/forms/input" 
          element={<InputExamples />} 
        />
        <Route 
          path="components/forms/number-input" 
          element={<NumberInputExamples />} 
        />
        <Route 
          path="components/forms/textarea" 
          element={<TextAreaExamples />} 
        />
        <Route 
          path="components/forms/select" 
          element={<SelectExamples />} 
        />
        <Route 
          path="components/forms/dropdown" 
          element={<DropdownExamples />} 
        />
        <Route 
          path="components/forms/listbox" 
          element={<ListboxExamples />} 
        />
        <Route 
          path="components/forms/checkbox" 
          element={<CheckboxExamples />} 
        />
        <Route 
          path="components/forms/radio-group" 
          element={<RadioGroupExamples />} 
        />
        <Route 
          path="components/forms/switch" 
          element={<SwitchExamples />} 
        />
        <Route 
          path="components/forms/slider" 
          element={<SliderExamples />} 
        />
        <Route 
          path="components/forms/date-input" 
          element={<DateInputExamples />} 
        />
        <Route 
          path="components/forms/time-input" 
          element={<TimeInputExamples />} 
        />
        <Route 
          path="components/forms/calendar" 
          element={<CalendarExamples />} 
        />
        <Route 
          path="components/forms/range-calendar" 
          element={<RangeCalendarExamples />} 
        />
        <Route 
          path="components/forms/date-picker" 
          element={<DatePickerExamples />} 
        />
        <Route 
          path="components/forms/date-range-picker" 
          element={<DateRangePickerExamples />} 
        />
        
        {/* Feedback & Overlay Component Examples */}
        <Route 
          path="components/feedback/modal" 
          element={<ModalExamples />} 
        />
        <Route 
          path="components/feedback/alert" 
          element={<AlertExamples />} 
        />
        <Route 
          path="components/feedback/toast" 
          element={<ToastExamples />} 
        />
        <Route 
          path="components/feedback/tooltip" 
          element={<TooltipExamples />} 
        />
        <Route 
          path="components/feedback/popover" 
          element={<PopoverExamples />} 
        />
        <Route 
          path="components/feedback/progress" 
          element={<ProgressExamples />} 
        />
        <Route 
          path="components/feedback/circular-progress" 
          element={<CircularProgressExamples />} 
        />
        <Route 
          path="components/feedback/spinner" 
          element={<SpinnerExamples />} 
        />
        <Route 
          path="components/feedback/skeleton" 
          element={<SkeletonExamples />} 
        />
        
        {/* Data Display Components */}
        <Route 
          path="components/data-display/code" 
          element={<CodeExamples />} 
        />
        <Route 
          path="components/data-display/image" 
          element={<ImageExamples />} 
        />
        <Route 
          path="components/data-display/kbd" 
          element={<KbdExamples />} 
        />
        <Route 
          path="components/data-display/snippet" 
          element={<SnippetExamples />} 
        />
        <Route 
          path="components/data-display/table" 
          element={<TableExamples />} 
        />
        
        {/* Theme System Playground - Tailwind v4 + HeroUI */}
        <Route 
          path="themes" 
          element={<V4ThemePlayground />} 
        />
        
        {/* Data Visualization Playground - AG Grid + Charts */}
        <Route 
          path="data" 
          element={<AGGridPlayground />} 
        />
      </Routes>
      </div>
    </PlaygroundThemeContext.Provider>
  );
};